import { cn, focusRing } from "@simpro/ui";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router";
import { formatDate } from "@/lib/formatDate";
import type { ArticleSummary } from "../schemas/articles.schemas";

export interface ArticleCardProps {
  article: ArticleSummary;
  /** The lead story at the top of the articles page: wide, its picture beside the words on a desktop. */
  featured?: boolean;
}

/**
 * One article as an editorial card (TDD-SA FR-SA-192; design note salon-booking-premium-redesign.md
 * section 5.3 items 11 and 14): its picture, its tag as an eyebrow with the date, the title in the
 * display face, the summary, and Read more as a text link whose underline draws in. The whole card is
 * the link's target; the picture zooms slowly under the pointer. Without a picture the card leads with
 * an ink panel and the title's initial in an outline, never an empty frame.
 */
export function ArticleCard({ article, featured = false }: ArticleCardProps) {
  return (
    <article
      className={cn(
        "group relative flex h-full flex-col gap-5",
        featured && "lg:grid lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:items-center lg:gap-14",
      )}
    >
      <div
        className={cn(
          "relative overflow-hidden",
          featured ? "rounded-card aspect-[4/3] md:aspect-[16/10] lg:rounded-stack" : "rounded-card aspect-[4/3]",
          article.image ? "bg-muted-bg" : "bg-ink",
        )}
      >
        {article.image ? (
          <img
            src={article.image.url}
            alt={article.image.altText}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 size-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.2,0.7,0.2,1)] group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          />
        ) : (
          <span aria-hidden="true" className="absolute inset-0 grid place-items-center">
            <span
              className={cn(
                "headline grid place-items-center rounded-full text-on-ink ring-1 ring-on-ink/40",
                featured ? "size-28 text-5xl" : "size-20 text-3xl",
              )}
            >
              {article.title.trim().charAt(0).toUpperCase()}
            </span>
          </span>
        )}
      </div>

      <div className={cn("flex flex-1 flex-col gap-3", featured && "lg:gap-5")}>
        <p className="eyebrow flex flex-wrap items-center gap-x-3 gap-y-1">
          {article.tag ? (
            <>
              <span className="text-accent-ink">{article.tag}</span>
              <span aria-hidden="true" className="h-px w-5 bg-edge" />
            </>
          ) : null}
          <time dateTime={article.publishedAt} className="text-muted tabular-nums">
            {formatDate(article.publishedAt)}
          </time>
        </p>
        <h3
          className={cn(
            "headline text-balance text-heading",
            featured ? "text-[clamp(1.75rem,3.2vw,3rem)] leading-[1.05]" : "text-[1.5rem] leading-[1.15] md:text-[1.625rem]",
          )}
        >
          {article.title}
        </h3>
        {article.summary ? (
          <p
            className={cn(
              "leading-relaxed text-subtle",
              featured ? "line-clamp-4 text-[15px] md:text-[17px]" : "line-clamp-3 text-[15px]",
            )}
          >
            {article.summary}
          </p>
        ) : null}
        <Link
          to={`/articles/${encodeURIComponent(article.id)}`}
          viewTransition
          className={cn(
            "mt-auto inline-flex min-h-11 items-center gap-2 self-start text-sm font-semibold text-heading",
            // The whole card is the link's target; the name stays "Read more: <title>".
            "after:absolute after:inset-0 after:content-['']",
            focusRing,
          )}
        >
          {/* The card's hover draws the underline: the stretched target sits over the words themselves. */}
          <span className="link-draw group-focus-within:[background-size:100%_1.5px] group-hover:[background-size:100%_1.5px]">
            Read more<span className="sr-only">: {article.title}</span>
          </span>
          <ArrowRight
            aria-hidden="true"
            className="size-4 transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none"
          />
        </Link>
      </div>
    </article>
  );
}
